import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Loader2, ArrowLeft, CheckCircle2, Circle, Upload, Trash2, FileText, Eye,
    ClipboardList, Scale, Users, Wrench, Building2, FileSignature, CheckCircle, AlertTriangle, RefreshCw, ShieldCheck, Package, Download,
    Save, Sparkles, ChevronDown, ChevronUp, FilePlus2, Calculator
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const fmtMoney = (n) => n == null ? '—' : 'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2 });

// Rehace el presupuesto al porcentaje de presentación (ej. 80%): aplica el factor
// a cada parcial, recalcula el PU y redondea al céntimo (como S10).
// ponytail: la suma de parciales redondeados puede desviarse céntimos del X% teórico;
// prevalece la coherencia interna (suma de parciales = total presentado).
function recalcularPct(chapters, pct) {
    const f = (Number(pct) || 100) / 100;
    let total = 0;
    const out = (chapters || []).map(ch => ({
        ...ch,
        items: (ch.items || []).map(it => {
            const q = Number(it.quantity) || 0;
            const base = it.total != null ? Number(it.total) : q * (Number(it.unit_price) || 0);
            const parcial = Math.round(base * f * 100) / 100;
            const pu = q > 0 ? Math.round((parcial / q) * 100) / 100 : Math.round((Number(it.unit_price) || 0) * f * 100) / 100;
            total += parcial;
            return { ...it, unit_price: pu, total: parcial };
        }),
    }));
    return { chapters: out, total: Math.round(total * 100) / 100 };
}

const CAT_ICON = {
    'Capacidad legal': Scale, 'Experiencia del postor': ClipboardList,
    'Experiencia del personal clave': Users, 'Experiencia del personal no clave': Users,
    'Formación académica del personal clave': Users, 'Equipamiento estratégico': Wrench,
    'Capacidad financiera': Building2,
};

const GRUPOS = [
    { key: 'admision', titulo: 'Documentos de Admisión', icon: CheckCircle, color: '#10b981', sub: 'Obligatorios — si falta uno, la oferta NO es admitida.' },
    { key: 'calificacion', titulo: 'Requisitos de Calificación', icon: ClipboardList, color: '#3b82f6', sub: 'Lo que debes acreditar para calificar.' },
    { key: 'perfeccionamiento', titulo: 'Para Perfeccionar el Contrato', icon: FileSignature, color: '#8b5cf6', sub: 'Documentos post-buena pro (si ganas).' },
];

export default function Expediente() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, getToken } = useAuth();
    const [exp, setExp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(null); // requisitoId en curso
    const [datos, setDatos] = useState(null);
    const [savingDatos, setSavingDatos] = useState(false);
    const [datosOpen, setDatosOpen] = useState(false);
    const [generables, setGenerables] = useState([]);
    const [generando, setGenerando] = useState(null); // tipo en curso
    const [eco, setEco] = useState(null);
    const [parsingS10, setParsingS10] = useState(false);
    const [savingEco, setSavingEco] = useState(false);
    const [pctPresentacion, setPctPresentacion] = useState(100); // % al que se presenta el presupuesto
    const [exportando, setExportando] = useState(false);
    const [cotizOpen, setCotizOpen] = useState(false); // tabla de cotización abierta
    const [importandoPrecios, setImportandoPrecios] = useState(false);
    const [sugiriendo, setSugiriendo] = useState(false);
    const [sugMap, setSugMap] = useState({}); // item_code|unit → { pu, n, sim } del banco

    const load = async () => {
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/expedientes/${id}`, { headers });
            const data = await res.json();
            if (data.ok) {
                setExp(data.expediente);
                setDatos(prev => prev || data.expediente.datosEmpresa || {});
                setEco(prev => prev || data.expediente.economico || {});
                if (data.expediente.economico?.ocr) setCotizOpen(true); // OCR aproximado → revisar precios

            }
            else setError(data.error || 'No se pudo cargar el expediente.');
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    useEffect(() => {
        (async () => {
            try {
                const headers = await getAuthHeaders();
                const res = await fetch(`${API_URL}/api/anexos-generables`, { headers });
                const data = await res.json();
                if (data.ok) setGenerables(data.anexos);
            } catch (e) { /* */ }
        })();
    }, []);

    const datosCompletos = datos && datos.razonSocial && datos.ruc && datos.representanteLegal && datos.dniRepresentante;

    const handleSaveDatos = async () => {
        setSavingDatos(true);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/expedientes/${id}/datos-empresa`, {
                method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(datos),
            });
            const data = await res.json();
            if (!data.ok) setError(data.error || 'No se pudieron guardar los datos.');
        } catch (e) { setError(e.message); }
        finally { setSavingDatos(false); }
    };

    const handleGenerar = async (tipo) => {
        if (!datosCompletos) { setDatosOpen(true); setError('Completa los datos del postor antes de generar anexos.'); return; }
        setGenerando(tipo);
        setError('');
        try {
            const headers = await getAuthHeaders();
            // guarda datos por si hay cambios sin guardar, luego genera
            await fetch(`${API_URL}/api/expedientes/${id}/datos-empresa`, { method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(datos) });
            const res = await fetch(`${API_URL}/api/expedientes/${id}/anexos/${tipo}/generar`, { method: 'POST', headers });
            const data = await res.json();
            if (data.ok) await load();
            else setError(data.error || 'No se pudo generar el anexo.');
        } catch (e) { setError(e.message); }
        finally { setGenerando(null); }
    };

    // Polling mientras el GEM esté auditando algún documento
    useEffect(() => {
        if (!exp) return;
        const auditando = (exp.documentos || []).some(d => d.estado === 'auditando');
        if (!auditando) return;
        const t = setTimeout(load, 4000);
        return () => clearTimeout(t);
    }, [exp]);

    const docsDe = (requisitoId) => (exp?.documentos || []).filter(d => d.requisitoId === requisitoId);

    const handleReaudit = async (docId) => {
        try {
            const headers = await getAuthHeaders();
            await fetch(`${API_URL}/api/expedientes/${id}/documentos/${docId}/auditar`, { method: 'POST', headers });
            await load();
        } catch (e) { setError(e.message); }
    };

    const handleUpload = async (requisitoId, grupo, file) => {
        if (!file) return;
        setUploading(requisitoId);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const fd = new FormData();
            fd.append('file', file);
            fd.append('requisitoId', requisitoId);
            fd.append('grupo', grupo);
            const res = await fetch(`${API_URL}/api/expedientes/${id}/documentos`, { method: 'POST', headers, body: fd });
            const data = await res.json();
            if (data.ok) await load();
            else setError(data.error || 'Error al subir el documento.');
        } catch (e) { setError(e.message); }
        finally { setUploading(null); }
    };

    const handleDeleteDoc = async (docId) => {
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/expedientes/${id}/documentos/${docId}`, { method: 'DELETE', headers });
            const data = await res.json();
            if (data.ok) await load();
            else setError(data.error || 'Error al eliminar.');
        } catch (e) { setError(e.message); }
    };

    const handleView = async (doc) => {
        const token = await getToken();
        window.open(`${API_URL}${doc.downloadUrl}?token=${token}`, '_blank');
    };

    const handleDownloadZip = async () => {
        const token = await getToken();
        window.open(`${API_URL}/api/expedientes/${id}/ensamblar?token=${token}`, '_blank');
    };

    const handleParseS10 = async (file) => {
        if (!file) return;
        setParsingS10(true);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_URL}/api/budget/parse`, { method: 'POST', headers, body: fd });
            const data = await res.json();
            if (data.ok) {
                const ocr = /OCR/i.test(data.stats?.method || '');
                setEco(e => ({ ...e, presupuestoTotal: data.stats.sum, verificado: data.stats.verified, costoDirecto: data.stats.costoDirecto, ocr, chapters: data.data?.chapters || [] }));
                if (ocr) setCotizOpen(true); // OCR aproximado → abrir la tabla para revisar
            }
            else setError(data.error || 'No se pudo leer el presupuesto S10.');
        } catch (e) { setError(e.message); }
        finally { setParsingS10(false); }
    };

    const handleSaveEco = async () => {
        setSavingEco(true);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/expedientes/${id}`, {
                method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ economico: eco }),
            });
            const data = await res.json();
            if (!data.ok) setError(data.error || 'No se pudo guardar.');
        } catch (e) { setError(e.message); }
        finally { setSavingEco(false); }
    };

    // Descarga el Excel S10 recalculado al % de presentación.
    const handleExportExcel = async () => {
        if (!eco?.chapters?.length) return;
        setExportando(true);
        setError('');
        try {
            const { chapters } = recalcularPct(eco.chapters, pctPresentacion);
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/budget/export`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ chapters, pct: pctPresentacion, nombre: exp?.proceso?.nomenclatura || exp?.proceso?.objeto || 'OFERTA' }),
            });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'No se pudo exportar'); }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `PRESUPUESTO_${pctPresentacion}PCT.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { setError(e.message); }
        finally { setExportando(false); }
    };

    // Edita el precio unitario de una partida y recalcula parcial + total del presupuesto.
    const setPU = (ci, ii, val) => {
        setEco(prev => {
            const chapters = prev.chapters.map((ch, x) => x !== ci ? ch : {
                ...ch,
                items: ch.items.map((it, y) => {
                    if (y !== ii) return it;
                    const pu = val === '' ? 0 : Number(val);
                    const total = Math.round((Number(it.quantity) || 0) * pu * 100) / 100;
                    return { ...it, unit_price: pu, total };
                }),
            });
            const presupuestoTotal = Math.round(chapters.reduce((s, c) => s + c.items.reduce((t, i) => t + (Number(i.total) || 0), 0), 0) * 100) / 100;
            return { ...prev, chapters, presupuestoTotal };
        });
    };

    // Importa precios desde un Excel YA cotizado: cruza por código de partida.
    const handleImportPrecios = async (file) => {
        if (!file || !eco?.chapters?.length) return;
        setImportandoPrecios(true);
        setError('');
        try {
            const headers = await getAuthHeaders();
            const fd = new FormData();
            fd.append('file', file);
            const res = await fetch(`${API_URL}/api/budget/parse`, { method: 'POST', headers, body: fd });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || 'No se pudo leer el Excel cotizado.');
            // Mapa código→PU del Excel cotizado
            const puPorCodigo = {};
            (data.data?.chapters || []).forEach(ch => (ch.items || []).forEach(it => {
                if (it.unit_price > 0) puPorCodigo[String(it.item_code).trim()] = Number(it.unit_price);
            }));
            let cruzados = 0;
            const chapters = eco.chapters.map(ch => ({
                ...ch,
                items: ch.items.map(it => {
                    const pu = puPorCodigo[String(it.item_code).trim()];
                    if (pu == null) return it;
                    cruzados++;
                    return { ...it, unit_price: pu, total: Math.round((Number(it.quantity) || 0) * pu * 100) / 100 };
                }),
            }));
            const presupuestoTotal = Math.round(chapters.reduce((s, c) => s + c.items.reduce((t, i) => t + (Number(i.total) || 0), 0), 0) * 100) / 100;
            setEco(e => ({ ...e, chapters, presupuestoTotal }));
            if (!cruzados) setError('Ningún código de partida coincidió con el Excel cotizado.');
        } catch (e) { setError(e.message); }
        finally { setImportandoPrecios(false); }
    };

    // Sugiere precios desde el banco (mediana de lo cotizado) a las partidas SIN precio.
    const handleSugerirPrecios = async () => {
        if (!eco?.chapters?.length) return;
        setSugiriendo(true);
        setError('');
        try {
            const partidas = [];
            eco.chapters.forEach(ch => ch.items.forEach(it => partidas.push({ descripcion: it.description, unidad: it.unit })));
            const headers = await getAuthHeaders();
            const res = await fetch(`${API_URL}/api/precios/sugerir`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ partidas }),
            });
            const data = await res.json();
            if (!data.ok) throw new Error(data.error || 'No se pudo sugerir');
            const sm = {};
            let idx = 0, aplicados = 0;
            const chapters = eco.chapters.map(ch => ({
                ...ch,
                items: ch.items.map(it => {
                    const sug = data.sugerencias[idx++];
                    if (sug?.pu != null) sm[it.item_code + '|' + it.unit] = { pu: sug.pu, n: sug.n, sim: sug.sim };
                    if ((it.unit_price || 0) > 0 || !sug?.pu) return it; // no pisar lo ya cotizado
                    aplicados++;
                    return { ...it, unit_price: sug.pu, total: Math.round((Number(it.quantity) || 0) * sug.pu * 100) / 100 };
                }),
            }));
            setSugMap(sm);
            setCotizOpen(true); // mostrar la tabla con las sugerencias y su confianza
            const presupuestoTotal = Math.round(chapters.reduce((s, c) => s + c.items.reduce((t, i) => t + (Number(i.total) || 0), 0), 0) * 100) / 100;
            setEco(e => ({ ...e, chapters, presupuestoTotal }));
            if (!aplicados && !Object.keys(sm).length) setError('El banco aún no tiene precios para estas partidas — se irá llenando con cada cotización que guardes.');
        } catch (e) { setError(e.message); }
        finally { setSugiriendo(false); }
    };

    if (loading) return <Center><Loader2 className="animate-spin" size={32} /></Center>;
    if (error && !exp) return <Center><div style={{ color: '#ef4444' }}>{error}</div></Center>;
    if (!exp) return null;

    const { proceso, resumen, checklist } = exp;

    // Aptitud para presentar (faltantes obligatorios y observados)
    const readiness = (() => {
        const faltan = [];
        let observados = 0;
        for (const g of ['admision', 'calificacion']) {
            for (const r of checklist?.[g] || []) {
                const opcional = r.deCorresponder === true || r.obligatorio === false;
                if (r.estado === 'observado') observados++;
                if (r.estado === 'pendiente' && !opcional) faltan.push(r.documento || r.categoria);
            }
        }
        return { faltan, observados, listo: faltan.length === 0 && observados === 0 };
    })();
    const tieneDocs = (exp.documentos || []).length > 0;

    return (
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <button onClick={() => navigate('/dashboard')} style={backBtn}>
                <ArrowLeft size={16} /> Volver
            </button>

            {/* Cabecera */}
            <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem', borderLeft: '4px solid hsl(var(--primary))' }}>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>{proceso?.objeto || 'Expediente'}</h1>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                    <Field label="Entidad" value={proceso?.entidad} />
                    <Field label="Nomenclatura" value={proceso?.nomenclatura} />
                    <Field label="Sistema" value={proceso?.sistemaContratacion} />
                    <Field label="Valor referencial" value={fmtMoney(proceso?.valorReferencial)} />
                </div>
                {/* Progreso */}
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                        <span style={{ fontWeight: 600 }}>Progreso del armado</span>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}>{resumen.cargados}/{resumen.total} requisitos · {resumen.progreso}%</span>
                    </div>
                    <div style={{ height: '10px', background: 'hsl(var(--muted))', borderRadius: '999px', overflow: 'hidden' }}>
                        <div style={{ width: `${resumen.progreso}%`, height: '100%', background: resumen.progreso === 100 ? '#10b981' : 'hsl(var(--primary))', transition: 'width 0.3s' }} />
                    </div>
                    {(resumen.aprobados > 0 || resumen.observados > 0) && (
                        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.78rem', marginTop: '0.5rem' }}>
                            <span style={{ color: '#10b981' }}>✓ {resumen.aprobados} aprobados por el GEM</span>
                            {resumen.observados > 0 && <span style={{ color: '#b45309' }}>⚠ {resumen.observados} observados</span>}
                        </div>
                    )}
                </div>
            </div>

            {error && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid #ef4444', borderRadius: 'var(--radius)', color: '#b91c1c', fontSize: '0.85rem' }}>
                    <AlertTriangle size={14} style={{ display: 'inline', marginRight: '0.4rem' }} />{error}
                </div>
            )}

            {/* ───────── Datos del postor + Generar anexos ───────── */}
            <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <button onClick={() => setDatosOpen(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Building2 size={18} color="hsl(var(--primary))" />
                        <h3 style={{ margin: 0 }}>Datos del postor</h3>
                        {!datosCompletos && <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.5rem', borderRadius: 999, background: 'rgba(245,158,11,0.15)', color: '#b45309' }}>Completar para generar anexos</span>}
                        {datosCompletos && <CheckCircle2 size={15} color="#10b981" />}
                    </span>
                    {datosOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>

                {datosOpen && datos && (
                    <div style={{ marginTop: '1.25rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem' }}>
                            <DatoInput label="Razón social *" value={datos.razonSocial} onChange={v => setDatos({ ...datos, razonSocial: v })} />
                            <DatoInput label="RUC *" value={datos.ruc} onChange={v => setDatos({ ...datos, ruc: v })} />
                            <DatoInput label="Representante legal *" value={datos.representanteLegal} onChange={v => setDatos({ ...datos, representanteLegal: v })} />
                            <DatoInput label="DNI del representante *" value={datos.dniRepresentante} onChange={v => setDatos({ ...datos, dniRepresentante: v })} />
                            <DatoInput label="Domicilio fiscal" value={datos.domicilioFiscal} onChange={v => setDatos({ ...datos, domicilioFiscal: v })} />
                            <DatoInput label="Correo electrónico" value={datos.correo} onChange={v => setDatos({ ...datos, correo: v })} />
                            <DatoInput label="Teléfono" value={datos.telefono} onChange={v => setDatos({ ...datos, telefono: v })} />
                            <DatoInput label="Partida registral" value={datos.partidaRegistral} onChange={v => setDatos({ ...datos, partidaRegistral: v })} />
                        </div>
                        <button onClick={handleSaveDatos} disabled={savingDatos} style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1.1rem', background: 'hsl(var(--primary))', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                            {savingDatos ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar datos
                        </button>
                    </div>
                )}

                {/* Generar anexos */}
                <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid hsl(var(--border))' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <Sparkles size={16} color="hsl(var(--accent))" />
                        <strong style={{ fontSize: '0.92rem' }}>Generar anexos automáticamente</strong>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.9rem' }}>
                        El Gem rellena estos formatos con los datos del postor y los adjunta al expediente, listos para revisar y firmar.
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                        {generables.map(a => (
                            <button key={a.tipo} onClick={() => handleGenerar(a.tipo)} disabled={generando === a.tipo}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500 }}>
                                {generando === a.tipo ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} color="hsl(var(--accent))" />}
                                Anexo {a.numero}: {a.titulo}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Grupos del checklist */}
            {GRUPOS.map(g => {
                const items = checklist?.[g.key] || [];
                if (!items.length) return null;
                return (
                    <div key={g.key} className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <g.icon size={18} color={g.color} />
                            <h3>{g.titulo}</h3>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>{g.sub}</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {items.map(item => (
                                <RequisitoRow
                                    key={item.id}
                                    item={item}
                                    grupo={g.key}
                                    anexos={exp.anexos}
                                    docs={docsDe(item.id)}
                                    uploading={uploading === item.id}
                                    onUpload={(file) => handleUpload(item.id, g.key, file)}
                                    onDeleteDoc={handleDeleteDoc}
                                    onView={handleView}
                                    onReaudit={handleReaudit}
                                />
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* ───────── Sobre Económico: congruencia cruzada ───────── */}
            {(() => {
                const p = eco?.presupuestoTotal;
                const c = eco?.cartaPropuesta;
                const tieneAmbos = p != null && c != null && c !== '';
                // ponytail: comparación al céntimo evita ruido de float
                const congruente = tieneAmbos && Math.round(p * 100) === Math.round(Number(c) * 100);
                const diff = tieneAmbos ? p - Number(c) : null;
                return (
                    <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <Calculator size={18} color="hsl(var(--primary))" />
                            <h3>Sobre Económico — Congruencia cruzada</h3>
                        </div>
                        <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.25rem' }}>
                            La suma del presupuesto (APU) debe coincidir EXACTO con tu Carta de Propuesta. Diferencia permitida: 0.00.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem', alignItems: 'end' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.3rem' }}>Presupuesto S10 (Excel o PDF)</label>
                                {p != null ? (
                                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                                        {fmtMoney(p)} {eco.verificado && <span style={{ fontSize: '0.68rem', color: '#10b981' }}>✓ al céntimo</span>}
                                    </div>
                                ) : (
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', border: '1px dashed hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.82rem' }}>
                                        {parsingS10 ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Leer presupuesto S10
                                        <input type="file" accept=".pdf,.xlsx,.xls" style={{ display: 'none' }} disabled={parsingS10}
                                            onChange={e => { if (e.target.files?.[0]) { handleParseS10(e.target.files[0]); e.target.value = ''; } }} />
                                    </label>
                                )}
                                {p != null && (
                                    <label style={{ display: 'inline-block', marginTop: '0.4rem', fontSize: '0.72rem', color: 'hsl(var(--primary))', cursor: 'pointer' }}>
                                        Reemplazar PDF
                                        <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) { handleParseS10(e.target.files[0]); e.target.value = ''; } }} />
                                    </label>
                                )}
                            </div>
                            <DatoInput label="Carta de Propuesta (S/)" value={eco?.cartaPropuesta} onChange={v => setEco({ ...eco, cartaPropuesta: v })} />
                            <button onClick={handleSaveEco} disabled={savingEco} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.6rem 1rem', background: 'hsl(var(--primary))', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', height: 'fit-content' }}>
                                {savingEco ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar
                            </button>
                        </div>
                        {tieneAmbos && (
                            <div style={{ ...readyBox(congruente ? '#10b981' : '#ef4444', congruente ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)'), marginTop: '1.25rem' }}>
                                {congruente ? <CheckCircle size={18} color="#10b981" /> : <AlertTriangle size={18} color="#ef4444" />}
                                <span>{congruente
                                    ? <><strong>Congruente.</strong> Presupuesto y Carta de Propuesta coinciden al céntimo.</>
                                    : <><strong>¡Descongruencia de S/ {Math.abs(diff).toLocaleString('es-PE', { minimumFractionDigits: 2 })}!</strong> El presupuesto y la Carta de Propuesta NO coinciden. Riesgo de descalificación.</>}</span>
                            </div>
                        )}

                        {/* Cotización: poner / ajustar precios por partida (la plantilla de OSCE viene sin precios) */}
                        {eco?.chapters?.length > 0 && (() => {
                            const sinPrecios = !eco.chapters.some(c => c.items.some(i => (i.unit_price || 0) > 0));
                            const totalCotiz = eco.chapters.reduce((s, c) => s + c.items.reduce((t, i) => t + (Number(i.total) || 0), 0), 0);
                            return (
                                <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px dashed hsl(var(--border))' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                        <FileText size={16} color="#3b82f6" />
                                        <strong style={{ fontSize: '0.9rem' }}>Cotizar partidas</strong>
                                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{eco.chapters.reduce((s, c) => s + c.items.length, 0)} partidas · total {fmtMoney(Math.round(totalCotiz * 100) / 100)}</span>
                                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <button onClick={handleSugerirPrecios} disabled={sugiriendo}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.7rem', border: 'none', borderRadius: 'var(--radius)', background: '#8b5cf6', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
                                                {sugiriendo ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Sugerir precios (banco)
                                            </button>
                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.7rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.78rem' }}>
                                                {importandoPrecios ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Importar (Excel cotizado)
                                                <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={importandoPrecios}
                                                    onChange={e => { if (e.target.files?.[0]) { handleImportPrecios(e.target.files[0]); e.target.value = ''; } }} />
                                            </label>
                                            <button onClick={() => setCotizOpen(o => !o)} style={{ padding: '0.4rem 0.7rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', background: 'transparent', cursor: 'pointer', fontSize: '0.78rem' }}>
                                                {cotizOpen ? 'Ocultar tabla' : 'Editar precios'}
                                            </button>
                                        </div>
                                    </div>
                                    {sinPrecios && (
                                        <div style={{ ...readyBox('#f59e0b', 'rgba(245,158,11,0.08)'), marginBottom: '0.75rem' }}>
                                            <AlertTriangle size={16} color="#f59e0b" />
                                            <span style={{ fontSize: '0.82rem' }}>El presupuesto vino <strong>sin precios</strong> (plantilla de metrados de OSCE). Pon el precio de cada partida —o importa tu Excel cotizado— para activar la presentación al %.</span>
                                        </div>
                                    )}
                                    {eco?.ocr && !sinPrecios && (
                                        <div style={{ ...readyBox('#f59e0b', 'rgba(245,158,11,0.08)'), marginBottom: '0.75rem' }}>
                                            <AlertTriangle size={16} color="#f59e0b" />
                                            <span style={{ fontSize: '0.82rem' }}>Este presupuesto se leyó por <strong>OCR de un escaneo</strong>: los precios y metrados son <strong>aproximados</strong>. Revísalos partida por partida (o importa tu Excel cotizado) antes de presentar.</span>
                                        </div>
                                    )}
                                    {cotizOpen && (
                                        <div style={{ maxHeight: '420px', overflowY: 'auto', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                                                <thead style={{ position: 'sticky', top: 0, background: 'hsl(var(--muted))' }}>
                                                    <tr style={{ textAlign: 'left' }}>
                                                        <th style={{ padding: '0.4rem 0.5rem' }}>Item</th>
                                                        <th style={{ padding: '0.4rem 0.5rem' }}>Descripción</th>
                                                        <th style={{ padding: '0.4rem 0.5rem' }}>Und</th>
                                                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Metrado</th>
                                                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>P.U. (S/)</th>
                                                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Parcial</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {eco.chapters.map((ch, ci) => (
                                                        <React.Fragment key={ch.code}>
                                                            <tr style={{ background: 'hsl(var(--muted))' }}>
                                                                <td style={{ padding: '0.35rem 0.5rem', fontWeight: 700 }}>{ch.code}</td>
                                                                <td colSpan={5} style={{ padding: '0.35rem 0.5rem', fontWeight: 700 }}>{ch.name}</td>
                                                            </tr>
                                                            {ch.items.map((it, ii) => (
                                                                <tr key={it.item_code + ii} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                                                                    <td style={{ padding: '0.3rem 0.5rem', color: 'hsl(var(--muted-foreground))' }}>{it.item_code}</td>
                                                                    <td style={{ padding: '0.3rem 0.5rem' }}>{it.description}</td>
                                                                    <td style={{ padding: '0.3rem 0.5rem' }}>{it.unit}</td>
                                                                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{Number(it.quantity).toLocaleString('es-PE')}</td>
                                                                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>
                                                                        <input type="number" min="0" step="0.01" value={it.unit_price || ''} placeholder="0.00"
                                                                            onChange={e => setPU(ci, ii, e.target.value)}
                                                                            style={{ width: '90px', padding: '0.25rem 0.4rem', textAlign: 'right', border: '1px solid hsl(var(--border))', borderRadius: '4px', fontSize: '0.78rem' }} />
                                                                        {(() => {
                                                                            const s = sugMap[it.item_code + '|' + it.unit];
                                                                            if (!s || s.pu == null) return null;
                                                                            const alta = s.sim >= 80 && s.n >= 3; // confianza
                                                                            const usado = Math.round((it.unit_price || 0) * 100) === Math.round(s.pu * 100);
                                                                            return (
                                                                                <div onClick={() => setPU(ci, ii, s.pu)} title="Usar este precio del banco"
                                                                                    style={{ fontSize: '0.62rem', marginTop: '2px', cursor: 'pointer', color: alta ? '#059669' : '#b45309', whiteSpace: 'nowrap' }}>
                                                                                    {usado ? '✓ ' : '💡 '}banco S/{Number(s.pu).toLocaleString('es-PE')} · {s.n}m · {s.sim}%
                                                                                </div>
                                                                            );
                                                                        })()}
                                                                    </td>
                                                                    <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(it.total)}</td>
                                                                </tr>
                                                            ))}
                                                        </React.Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {cotizOpen && (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                                            <button onClick={handleSaveEco} disabled={savingEco} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.1rem', background: '#10b981', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem' }}>
                                                {savingEco ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar cotización
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Motor: presentar el presupuesto a un % (clásico "preséntalo al 80%") */}
                        {eco?.chapters?.some(c => c.items.some(i => (i.unit_price || 0) > 0)) && (() => {
                            const rec = recalcularPct(eco.chapters, pctPresentacion);
                            const baja = p != null ? p - rec.total : null;
                            return (
                                <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px dashed hsl(var(--border))' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                        <Sparkles size={16} color="#8b5cf6" />
                                        <strong style={{ fontSize: '0.9rem' }}>Presentar a un porcentaje</strong>
                                        <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>— el sistema rehace cada precio al % que indiques.</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.3rem' }}>% de presentación</label>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                                <input type="number" min="1" max="150" step="0.5" value={pctPresentacion}
                                                    onChange={e => setPctPresentacion(e.target.value === '' ? '' : Number(e.target.value))}
                                                    style={{ width: '90px', padding: '0.5rem 0.6rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', fontSize: '0.95rem', fontWeight: 600 }} />
                                                <span style={{ fontWeight: 700 }}>%</span>
                                                {[80, 85, 90, 95, 100].map(v => (
                                                    <button key={v} onClick={() => setPctPresentacion(v)}
                                                        style={{ padding: '0.3rem 0.55rem', fontSize: '0.72rem', borderRadius: '999px', border: '1px solid hsl(var(--border))', background: pctPresentacion === v ? 'hsl(var(--primary))' : 'transparent', color: pctPresentacion === v ? '#fff' : 'hsl(var(--muted-foreground))', cursor: 'pointer' }}>{v}%</button>
                                                ))}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '180px' }}>
                                            <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Presupuesto al {pctPresentacion || 0}%</div>
                                            <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#8b5cf6' }}>{fmtMoney(rec.total)}</div>
                                            {baja != null && pctPresentacion < 100 && <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>Rebaja: {fmtMoney(baja)}</div>}
                                        </div>
                                        <button onClick={handleExportExcel} disabled={exportando}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.1rem', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                                            {exportando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Descargar Excel S10 al {pctPresentacion || 0}%
                                        </button>
                                    </div>
                                    {/* Resumen por capítulo recalculado */}
                                    <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                            <thead>
                                                <tr style={{ textAlign: 'left', color: 'hsl(var(--muted-foreground))', borderBottom: '1px solid hsl(var(--border))' }}>
                                                    <th style={{ padding: '0.4rem 0.5rem' }}>Cap.</th>
                                                    <th style={{ padding: '0.4rem 0.5rem' }}>Descripción</th>
                                                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Original</th>
                                                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Al {pctPresentacion || 0}%</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rec.chapters.map((ch, i) => {
                                                    const orig = (eco.chapters[i]?.items || []).reduce((s, it) => s + (Number(it.total) || 0), 0);
                                                    const nuevo = (ch.items || []).reduce((s, it) => s + (Number(it.total) || 0), 0);
                                                    return (
                                                        <tr key={ch.code} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                                                            <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{ch.code}</td>
                                                            <td style={{ padding: '0.4rem 0.5rem' }}>{ch.name}</td>
                                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: 'hsl(var(--muted-foreground))' }}>{fmtMoney(orig)}</td>
                                                            <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmtMoney(nuevo)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                );
            })()}

            {/* ───────── Ensamblaje y descarga ───────── */}
            <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <Package size={18} color="hsl(var(--primary))" />
                    <h3>Ensamblaje y descarga</h3>
                </div>
                <p style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1.25rem' }}>
                    Empaqueta todos los documentos en un ZIP organizado por sobre, con nomenclatura PLADICOP (sin tildes ni espacios), listo para SEACE.
                </p>

                {/* Estado de aptitud */}
                {readiness.listo ? (
                    <div style={readyBox('#10b981', 'rgba(16,185,129,0.08)')}>
                        <CheckCircle size={18} color="#10b981" />
                        <span><strong>Listo para presentar.</strong> Todos los requisitos obligatorios están cargados y aprobados por el GEM.</span>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem' }}>
                        {readiness.faltan.length > 0 && (
                            <div style={readyBox('#f59e0b', 'rgba(245,158,11,0.08)')}>
                                <AlertTriangle size={18} color="#f59e0b" />
                                <span><strong>{readiness.faltan.length} requisito(s) obligatorio(s) sin documento:</strong> {readiness.faltan.slice(0, 4).join('; ')}{readiness.faltan.length > 4 ? '…' : ''}</span>
                            </div>
                        )}
                        {readiness.observados > 0 && (
                            <div style={readyBox('#ef4444', 'rgba(239,68,68,0.08)')}>
                                <AlertTriangle size={18} color="#ef4444" />
                                <span><strong>{readiness.observados} documento(s) observado(s) por el GEM.</strong> Revísalos antes de presentar.</span>
                            </div>
                        )}
                    </div>
                )}

                <button onClick={handleDownloadZip} disabled={!tieneDocs}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', background: tieneDocs ? 'hsl(var(--primary))' : 'hsl(var(--muted))', color: 'white', border: 'none', borderRadius: 'var(--radius)', cursor: tieneDocs ? 'pointer' : 'not-allowed', fontWeight: 600 }}>
                    <Download size={18} /> Descargar expediente (ZIP)
                </button>
                {!tieneDocs && <p style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>Carga al menos un documento para poder ensamblar.</p>}
            </div>
        </div>
    );
}

const readyBox = (color, bg) => ({ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.85rem 1rem', borderRadius: 'var(--radius)', background: bg, border: `1px solid ${color}`, fontSize: '0.85rem', marginBottom: '0' });

function DatoInput({ label, value, onChange }) {
    return (
        <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.3rem' }}>{label}</label>
            <input value={value || ''} onChange={e => onChange(e.target.value)}
                style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 'var(--radius)', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.85rem' }} />
        </div>
    );
}

function EstadoIcon({ estado }) {
    switch (estado) {
        case 'auditando': return <Loader2 size={18} className="animate-spin" color="#3b82f6" />;
        case 'auditado_ok': return <CheckCircle2 size={18} color="#10b981" />;
        case 'observado': return <AlertTriangle size={18} color="#f59e0b" />;
        case 'cargado': return <CheckCircle2 size={18} color="hsl(var(--muted-foreground))" />;
        default: return <Circle size={18} color="hsl(var(--muted-foreground))" />;
    }
}

const DOC_BADGE = {
    auditando: { txt: 'Auditando…', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    auditado_ok: { txt: '✓ Aprobado', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    observado: { txt: '⚠ Observado', color: '#b45309', bg: 'rgba(245,158,11,0.15)' },
    revisar: { txt: 'Revisar', color: 'hsl(var(--muted-foreground))', bg: 'hsl(var(--muted))' },
    cargado: { txt: 'Cargado', color: 'hsl(var(--muted-foreground))', bg: 'hsl(var(--muted))' },
};

function RequisitoRow({ item, grupo, anexos, docs, uploading, onUpload, onDeleteDoc, onView, onReaudit }) {
    const inputRef = useRef(null);
    const [openAudit, setOpenAudit] = useState(null); // docId con auditoría expandida
    const activo = item.estado && item.estado !== 'pendiente';
    const Ico = CAT_ICON[item.categoria] || null;
    const anexoTitle = item.anexo && anexos?.find(a => a.numero === item.anexo)?.titulo;
    const titulo = item.documento || item.categoria;
    const bg = item.estado === 'observado' ? 'rgba(245,158,11,0.05)'
        : item.estado === 'auditado_ok' ? 'rgba(16,185,129,0.05)' : 'transparent';

    return (
        <div style={{ border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', padding: '0.85rem', background: bg }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <div style={{ marginTop: '2px', flexShrink: 0 }}>
                    <EstadoIcon estado={item.estado} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                        {item.letra && <strong style={{ color: 'hsl(var(--primary))', marginRight: '0.4rem' }}>{item.letra})</strong>}
                        {Ico && <Ico size={13} style={{ display: 'inline', marginRight: '0.3rem', verticalAlign: 'middle', color: 'hsl(var(--muted-foreground))' }} />}
                        {item.categoria && grupo === 'calificacion' && <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>[{item.categoria}] </span>}
                        {titulo}
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                        {item.anexo && <Tag>Anexo {item.anexo}{anexoTitle ? `: ${anexoTitle.slice(0, 40)}` : ''}</Tag>}
                        {item.montoRequerido && <Tag amber>{fmtMoney(item.montoRequerido)}</Tag>}
                        {item.deCorresponder && <Tag amber>de corresponder</Tag>}
                    </div>

                    {/* Documentos cargados */}
                    {docs.length > 0 && (
                        <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                            {docs.map(d => {
                                const badge = DOC_BADGE[d.estado] || DOC_BADGE.cargado;
                                const tieneReporte = d.auditoria?.texto && (d.estado === 'observado' || d.estado === 'revisar');
                                return (
                                    <div key={d.id} style={{ background: 'hsl(var(--muted))', borderRadius: 'var(--radius)', padding: '0.35rem 0.6rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem' }}>
                                            <FileText size={13} style={{ flexShrink: 0 }} />
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nombre}</span>
                                            {d.generado && <span style={{ fontSize: '0.62rem', padding: '0.05rem 0.4rem', borderRadius: 999, background: 'rgba(150,90,255,0.15)', color: '#7c3aed', whiteSpace: 'nowrap' }}>generado</span>}
                                            <span style={{ fontSize: '0.68rem', padding: '0.1rem 0.45rem', borderRadius: '999px', background: badge.bg, color: badge.color, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                                                {d.estado === 'auditando' && <Loader2 size={10} className="animate-spin" />}{badge.txt}
                                            </span>
                                            {tieneReporte && (
                                                <button onClick={() => setOpenAudit(openAudit === d.id ? null : d.id)} style={iconBtn} title="Ver auditoría"><ShieldCheck size={14} /></button>
                                            )}
                                            {(d.estado === 'observado' || d.estado === 'revisar') && (
                                                <button onClick={() => onReaudit(d.id)} style={iconBtn} title="Re-auditar"><RefreshCw size={13} /></button>
                                            )}
                                            <button onClick={() => onView(d)} style={iconBtn} title="Ver PDF"><Eye size={14} /></button>
                                            <button onClick={() => onDeleteDoc(d.id)} style={{ ...iconBtn, color: '#ef4444' }} title="Eliminar"><Trash2 size={14} /></button>
                                        </div>
                                        {tieneReporte && openAudit === d.id && (
                                            <div style={{ marginTop: '0.4rem', padding: '0.5rem', background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: '0.74rem', whiteSpace: 'pre-wrap', color: 'hsl(var(--foreground))', maxHeight: '260px', overflowY: 'auto' }}>
                                                {d.auditoria.texto}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Botón subir */}
                <div style={{ flexShrink: 0 }}>
                    <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.zip" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files?.[0]) { onUpload(e.target.files[0]); e.target.value = ''; } }} />
                    <button onClick={() => inputRef.current?.click()} disabled={uploading} style={uploadBtn}>
                        {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {docs.length > 0 ? 'Otro' : 'Subir'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, value }) => (
    <div>
        <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.72rem', marginBottom: '0.15rem' }}>{label}</div>
        <div style={{ fontWeight: 600 }}>{value || '—'}</div>
    </div>
);
const Tag = ({ children, amber }) => (
    <span style={{ fontSize: '0.68rem', padding: '0.1rem 0.45rem', borderRadius: '999px', background: amber ? 'rgba(245,158,11,0.15)' : 'hsl(var(--muted))', color: amber ? '#b45309' : 'hsl(var(--muted-foreground))' }}>{children}</span>
);
const Center = ({ children }) => <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>{children}</div>;
const backBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', color: 'hsl(var(--muted-foreground))', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.85rem' };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', padding: '0.15rem', display: 'flex' };
const uploadBtn = { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 0.8rem', background: 'hsl(var(--primary))', color: 'white', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: '0.78rem', whiteSpace: 'nowrap' };
